import React from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { ListComponent } from "@auto/widgets/ListComponent";
import { getServiceUri } from "@auto/metadata";
import { rest, t } from "@auto/services";
import { operatorRoleLabelKey, operatorScopeLabelKey, operatorTerritory } from "./operatorTerritory";
import {
  OPERATOR_REQUEST_FACETS,
  normalizeOperatorRequestFilters
} from "./OperatorAccountRequestListFacets";


const PAGE_SIZE = 50;

const dateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const buildSort = (sort) => (sort || [])
  .map(({ field, direction, column }) => ({
    selector: column?.sortSelector || field,
    direction
  }))
  .filter(({ selector, direction }) => selector && ["asc", "desc"].includes(direction));

const request = (path, payload) =>
  rest.request(`${getServiceUri()}operator-account-request-list/${path}`, "POST", payload);

const OperatorAccountRequestListPage = ({ gui }) => {
  const projection = React.useMemo(() => ({
    storageKey: "security.operator-account-request",
    facets: OPERATOR_REQUEST_FACETS,
    columns: [
      { title: "person.lastName", field: "fullName", sortSelector: "lastName" },
      { title: "user.username", field: "username", sortSelector: "username" },
      { title: "person.nationalId", field: "nationalId", sortSelector: "nationalId" },
      {
        title: "operator.type", field: "operatorType", sortSelector: "operatorType",
        render: (row) => t(operatorRoleLabelKey(operatorTerritory(row).role))
      },
      {
        title: "operator.scope", field: "territoryLabel", sorting: false,
        render: (row) => {
          const scope = operatorTerritory(row);
          return scope.valid
            ? `${t(operatorScopeLabelKey(scope.role))}: ${scope.territoryLabel}`
            : t("operator.scope.unassigned");
        }
      },
      { title: "user.requestStatus", field: "requestStatus", sortSelector: "requestStatus" },
      { title: "voter.submittedAt", field: "submittedAtFormatted", sortSelector: "submittedAt" },
      { title: "Actions", field: "__actions", sorting: false, filtering: false }
    ],
    actions: (row, currentGui) => (
      <Tooltip title={t("View")}>
        <IconButton size="small" aria-label={t("View")}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            currentGui.goTo(`/operator-account-request/${row.gid}`);
          }}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    fetch: async (query) => {
      const filters = normalizeOperatorRequestFilters(query?.filters);
      const payload = {
        search: query?.search || "",
        filters,
        sort: buildSort(query?.sort),
        limit: query?.pageSize || PAGE_SIZE,
        offset: (query?.page || 0) * PAGE_SIZE
      };
      const [rows, totalCount] = await Promise.all([
        request("search", payload),
        request("count", { search: payload.search, filters })
      ]);
      return {
        data: (rows || []).map((row) => ({
          ...row,
          fullName: [row.firstName, row.lastName].filter(Boolean).join(" "),
          submittedAtFormatted: dateTime(row.submittedAt)
        })),
        totalCount, page: query?.page || 0
      };
    }
  }), []);

  return <ListComponent gui={gui} projection={projection} />;
};

export const displayOperatorAccountRequestList = (gui) => ({
  uuid: "operator-account-request-list",
  view: () => <OperatorAccountRequestListPage gui={gui} />
});

export default displayOperatorAccountRequestList;
